import { FilterLiveForm, useListContext } from "ra-core";
import { SearchInput, type SearchInputProps } from "@/components/admin";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Filter, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const FlexForm = (props: React.FormHTMLAttributes<HTMLFormElement>) => (
  <form className="flex-1" {...props} />
);

const SEARCH_CATEGORIES = [
  { label: "All", value: "q", placeholder: "Search name, company..." },
  { label: "Name", value: "q", placeholder: "Search by name..." },
  { label: "Company", value: "company_name", placeholder: "Search by company..." },
  { label: "Phone", value: "phone_fts", placeholder: "Search by phone..." },
  { label: "Email", value: "email_fts", placeholder: "Search by email..." },
  { label: "Address", value: "address", placeholder: "Search by address..." },
];

export type CollapsedIcon = {
  icon: React.ReactNode;
  label: string;
  color?: string;
};

export const ResponsiveFilters = ({
  children,
  searchInput,
  collapsedIcons,
}: {
  children: React.ReactNode;
  searchInput?: Partial<SearchInputProps>;
  collapsedIcons?: CollapsedIcon[];
}) => {
  const {
    source = "q",
    className,
    ...otherSearchInputProps
  } = searchInput || {};
  const isMobile = useIsMobile();
  const { setFilters, filterValues } = useListContext();
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalNode(document.getElementById("filter-sidebar-portal-target"));
  }, []);

  const activeFiltersCount = Object.entries(filterValues || {}).filter(
    ([key]) => key !== source,
  ).length;

  const handleClearFilters = () => {
    const searchValue = filterValues[source];
    const preservedFilters = searchValue ? { [source]: searchValue } : {};
    setFilters(preservedFilters, []);
  };

  if (isMobile) {
    return (
      <div className="flex flex-1 gap-2">
        <FilterLiveForm formComponent={FlexForm}>
          <SearchInput
            source={source}
            className={className}
            {...otherSearchInputProps}
          />
        </FilterLiveForm>
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative size-9"
              aria-label="Filter"
            >
              <Filter className="size-5" />
              {activeFiltersCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs flex items-center justify-center"
                >
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-dvh p-4 flex flex-col">
            <SheetHeader className="-p-4">
              <SheetTitle>
                <h1 className="text-xl font-semibold">Filters</h1>
              </SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto flex flex-col gap-3 pb-4">
              {children}
            </div>
            <SheetFooter className="-p-4 relative">
              <div className="absolute -top-12 left-0 right-0 h-8 bg-gradient-to-t from-background to-transparent pointer-events-none" />
              <div className="flex w-full gap-4">
                <SheetClose asChild>
                  <Button
                    onClick={handleClearFilters}
                    type="button"
                    variant="secondary"
                    className="flex-1"
                  >
                    Clear filters
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button className="flex-1">Apply</Button>
                </SheetClose>
              </div>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  const desktopContent = (
    <div
      className={`order-first transition-all duration-300 ease-in-out shrink-0 ${filterCollapsed ? "w-20" : "w-64"
        }`}
    >
      {/* Sidebar styled container - using tertiary color and docked layout */}
      <div className="erp-sidebar erp-sidebar-cream flex-1 flex flex-col overflow-hidden !ml-0 !rounded-l-none !border-l-0">
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-3 py-3 border-b border-border/80 shrink-0 bg-transparent z-10 w-full">
          {!filterCollapsed && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500 dark:text-slate-400" /> Filters
                {activeFiltersCount > 0 && (
                  <Badge variant="destructive" className="h-5 w-5 p-0 text-xs flex items-center justify-center bg-destructive text-destructive-foreground">
                    {activeFiltersCount}
                  </Badge>
                )}
              </span>
              {activeFiltersCount > 0 && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="text-[11px] font-bold text-destructive hover:underline cursor-pointer transition-all ml-1.5 shrink-0"
                >
                  (Clear all)
                </button>
              )}
            </div>
          )}
          <button
            onClick={() => setFilterCollapsed(!filterCollapsed)}
            className="p-1.5 rounded-lg hover:bg-slate-200/50 dark:hover:bg-slate-800 transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
            aria-label={filterCollapsed ? "Expand filters" : "Collapse filters"}
          >
            {filterCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Sidebar Body - expanded */}
        {!filterCollapsed && (
          <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-4 scrollbar-hide pb-32">
            {children}
          </div>
        )}

        {/* Collapsed icon strip */}
        {filterCollapsed && (
          <div className="flex-1 overflow-y-auto flex flex-col items-center gap-2 pt-3 px-1 scrollbar-hide">
            {collapsedIcons?.map((item, idx) => (
              <button
                key={idx}
                onClick={() => setFilterCollapsed(false)}
                className="p-2 rounded-lg transition-colors text-slate-500 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-200 relative group"
                title={item.label}
              >
                <span className={item.color ? '' : ''}>{item.icon}</span>
                {item.color && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-card"
                    style={{ backgroundColor: item.color }}
                  />
                )}
                {/* Tooltip */}
                <span className="absolute left-full ml-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="md:hidden">
        {/* Mobile Filter Sheet Component removed from DOM on desktop but code implies full mobile behavior stays if isMobile */}
        {/* This return structure is primarily for desktop - mobile is handled by the early return */}
      </div>
      {portalNode ? createPortal(desktopContent, portalNode) : desktopContent}
    </>
  );
};

/** Standalone search bar with dropdown category selector*/
export const LeadsSearchBar = () => {
  const [searchCategory, setSearchCategory] = useState(SEARCH_CATEGORIES[0]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative flex items-center w-72" ref={dropdownRef}>
      {/* Search category dropdown pill button */}
      <button
        type="button"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center gap-1 px-3 h-9 text-xs font-medium border border-r-0 border-input rounded-l-lg bg-muted/50 text-muted-foreground hover:bg-muted transition-colors whitespace-nowrap shrink-0 cursor-pointer"
      >
        {searchCategory.label}
        <ChevronDown className="w-3 h-3" />
      </button>
      {/* Dropdown list */}
      {dropdownOpen && (
        <div className="absolute top-11 left-0 z-50 w-40 bg-popover border border-border rounded-xl shadow-lg py-1 flex flex-col animate-in fade-in slide-in-from-top-1 duration-150">
          {SEARCH_CATEGORIES.map((cat) => (
            <button
              key={cat.label}
              onClick={() => {
                setSearchCategory(cat);
                setDropdownOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors ${searchCategory.label === cat.label ? "bg-primary/10 text-primary font-semibold" : "text-foreground"
                }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}
      <FilterLiveForm formComponent={FlexForm}>
        <SearchInput
          source={searchCategory.value}
          placeholder={searchCategory.placeholder}
          className="flex-1 [&_input]:rounded-l-none [&_input]:rounded-r-lg [&_input]:border-l-0 [&_input]:h-9 sm:ml-0 ml-0 sm:w-auto w-auto"
        />
      </FilterLiveForm>
    </div>
  );
};

const COMPANY_SEARCH_CATEGORIES = [
  { label: "All", value: "q", placeholder: "Search company name..." },
  { label: "Sector", value: "sector@ilike", placeholder: "Search by sector..." },
];

export const CompanySearchBar = () => {
  const [searchCategory, setSearchCategory] = useState(COMPANY_SEARCH_CATEGORIES[0]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative flex items-center w-72" ref={dropdownRef}>
      {/* Search category dropdown pill button */}
      <button
        type="button"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center gap-1 px-3 h-9 text-xs font-medium border border-r-0 border-input rounded-l-lg bg-muted/50 text-muted-foreground hover:bg-muted transition-colors whitespace-nowrap shrink-0 cursor-pointer"
      >
        {searchCategory.label}
        <ChevronDown className="w-3 h-3" />
      </button>
      {/* Dropdown list */}
      {dropdownOpen && (
        <div className="absolute top-11 left-0 z-50 w-40 bg-popover border border-border rounded-xl shadow-lg py-1 flex flex-col animate-in fade-in slide-in-from-top-1 duration-150">
          {COMPANY_SEARCH_CATEGORIES.map((cat) => (
            <button
              key={cat.label}
              onClick={() => {
                setSearchCategory(cat);
                setDropdownOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors ${searchCategory.label === cat.label ? "bg-primary/10 text-primary font-semibold" : "text-foreground"
                }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}
      <FilterLiveForm formComponent={FlexForm}>
        <SearchInput
          source={searchCategory.value}
          placeholder={searchCategory.placeholder}
          className="flex-1 [&_input]:rounded-l-none [&_input]:rounded-r-lg [&_input]:border-l-0 [&_input]:h-9 sm:ml-0 ml-0 sm:w-auto w-auto"
        />
      </FilterLiveForm>
    </div>
  );
};
